import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import api from './services/apiService';
import { Send, Bot, MessageSquare, Bookmark, Search, Film, MessageCircle, Calendar, BookOpen, Settings, LogOut } from 'lucide-react';
import MessageBubble from './components/chat/MessageBubble';
import VideoAnalysisUpload from './VideoAnalysisUpload';
import PlanningTab from './components/PlanningTab';
import NotesTab from './components/NotesTab';
import SettingsModal from './components/SettingsModal';
import MobileBottomNav from './components/MobileBottomNav';
import MobileFAB from './components/MobileFAB';
import SubscriptionGate from './components/SubscriptionGate';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { useAuth } from './hooks/useAuth';
import { useIsMobile } from './hooks/useIsMobile';
import LandingPage from './pages/LandingPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AboutPage from './pages/AboutPage';
import SampleAnalysis from './pages/SampleAnalysis';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import './App.css';

/**
 * Generate a local ISO string without timezone suffix (user's wall-clock time).
 */
function localISOString() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * Parse a conversation text string into user and lumi parts.
 * Handles both USER:/LUMI: and User:/Lumi: formats.
 * Returns { userPart, lumiPart } or null if format is unrecognized.
 */
function parseConversationText(text) {
  if (!text) return null;
  if (text.includes('USER:') && text.includes('LUMI:')) {
    const parts = text.split('LUMI:');
    const userPart = parts[0].replace('USER:', '').trim();
    const lumiPart = parts.length > 1 ? parts[1].trim() : '';
    return userPart && lumiPart ? { userPart, lumiPart } : null;
  }
  if (text.includes('User:') && text.includes('Lumi:')) {
    const parts = text.split('Lumi:');
    const userPart = parts[0].replace('User:', '').trim();
    const lumiPart = parts.length > 1 ? parts[1].trim() : '';
    return userPart && lumiPart ? { userPart, lumiPart } : null;
  }
  return null;
}

function MainApp() {
  const { user, logout, loading: authLoading } = useAuth();
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState([
    {
      sender: 'lumi',
      text: `Hi! I'm **Lumi**, your Olympic Figure Skating Virtual Assistant! 🏆

I'm here to help you with:
- **Mental preparation** strategies
- **Performance analysis** and feedback
- **Goal setting and planning** and progress tracking
- **Video analysis** for technique improvement to upload your skating videos and get detailed feedback tap Skate icon or Click Video Analysis to upload your video and get started

What would you like to work on today?`,
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef(null);
  const [bookmarksVisible, setBookmarksVisible] = useState(false);
  const [bookmarkedConversations, setBookmarkedConversations] = useState([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'video'
  const [currentVideoId, setCurrentVideoId] = useState(null); // Track current video being viewed
  const [settingsOpen, setSettingsOpen] = useState(false); // Settings modal state
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Video analysis handlers
  const handleAnalysisStart = (elementType) => {
    const startMessage = {
      sender: 'lumi',
      text: `🎥 Starting video analysis for **${elementType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}**... This may take a few minutes.`,
      timestamp: new Date(),
      isAnalysis: true
    };
    setMessages(prev => [...prev, startMessage]);
    // Keep user on Video Analysis tab to see synchronized features
    // setActiveTab('chat'); // REMOVED - don't switch away from video analysis
  };

  // Handler when video is loaded (either new upload or history)
  const handleVideoLoaded = (videoId) => {
    console.log('📹 Video loaded:', videoId);
    setCurrentVideoId(videoId);
  };

  // Load video conversation into chat when switching tabs
  const loadVideoConversation = async (videoId) => {
    if (!videoId) return;

    try {
      const videoEventId = `video_${videoId}`;
      console.log('📥 Loading video conversation for chat:', videoEventId);

      const response = await api.get(`/events/${videoEventId}`);

      if (response.data && response.data.found) {
        const event = response.data.event;
        const conversationText = JSON.parse(event.full_details).conversation;

        // Check if this conversation is already in messages
        const alreadyLoaded = messages.some(msg => msg.eventId === event.id);
        if (alreadyLoaded) {
          console.log('✓ Video conversation already in chat');
          return;
        }

        // Parse conversation into user and lumi parts
        const parsed = parseConversationText(conversationText);
        if (parsed) {
          // Add user message
          const userMessage = {
            sender: 'user',
            text: parsed.userPart,
            timestamp: new Date(event.event_date),
            eventId: event.id,
            bookmarked: event.bookmarked || false
          };

          // Add lumi response
          const lumiMessage = {
            sender: 'lumi',
            text: parsed.lumiPart,
            timestamp: new Date(event.event_date),
            eventId: event.id,
            bookmarked: event.bookmarked || false,
            isAnalysis: true
          };

          console.log('📝 Adding video conversation to chat');
          setMessages(prev => [...prev, userMessage, lumiMessage]);
          scrollToBottom();
        }
      } else {
        console.warn('⚠️  Video analysis not found in database:', videoEventId);
      }
    } catch (error) {
      console.error('❌ Error loading video conversation:', error);
    }
  };

  const handleAnalysisComplete = async (result) => {
    console.log('🎉 handleAnalysisComplete called with:', result);

    // Video analysis is complete - user is on Video Analysis tab
    // They can review the synchronized features and overlay video
    // When they click "Coaching" tab, loadVideoConversation will add it to chat

    // No need to add to chat here - it will be added when user clicks Coaching tab
    console.log('✅ Video analysis complete - ready to view on Video Analysis tab');
    console.log('💡 Click "Coaching" tab to see the analysis conversation');
  };

  // Load conversation history from backend on startup
  useEffect(() => {
    const loadConversationHistory = async () => {
      if (historyLoaded) return;
      
      try {
        const response = await api.get(`/database-conversations?limit=5`);
        const conversations = response.data.conversations;
        
        if (conversations && conversations.length > 0) {
          const historyMessages = [];
          
          for (let i = conversations.length - 1; i >= 0; i--) {
            const conversation = conversations[i];
            const parsed = parseConversationText(conversation.conversation);
            if (!parsed) continue;

            historyMessages.push({
              sender: 'user',
              text: parsed.userPart,
              timestamp: new Date(conversation.event_date),
              eventId: conversation.id
            });
            historyMessages.push({
              sender: 'lumi',
              text: parsed.lumiPart,
              timestamp: new Date(conversation.event_date),
              eventId: conversation.id
            });
          }

          if (historyMessages.length > 0) {
            const welcomeMessage = messages[0];
            setMessages([welcomeMessage, ...historyMessages]);
          }
        }
        setHistoryLoaded(true);
      } catch (error) {
        console.error('Error loading conversation history:', error);
        setHistoryLoaded(true);
      }
    };

    loadConversationHistory();
  }, [historyLoaded, messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [input]);

  // Cleanup search debounce timer on unmount
  useEffect(() => {
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, []);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    setLoading(true);

    setTimeout(() => adjustTextareaHeight(), 0);

    setMessages(prev => [...prev, {
      sender: 'user',
      text: userMessage,
      timestamp: new Date(),
      eventId: null
    }]);

    try {
      const response = await api.post(`/chat`, {
        message: userMessage,
        timestamp: localISOString()
      });

      setMessages(prev => [...prev, {
        sender: 'lumi',
        text: response.data.response,
        timestamp: new Date(),
        eventId: response.data.event_id
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        sender: 'lumi', 
        text: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const isToday = timestamp.toDateString() === now.toDateString();
    if (isToday) {
      return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return timestamp.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' ' + timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const exportToMarkdown = (userMsg, lumiMsg) => {
    const userText = userMsg.text;
    const lumiText = lumiMsg.text;
    const timestamp = lumiMsg.timestamp.toLocaleString();

    const markdown = `# Conversation with Lumi
**Date:** ${timestamp}

## Your Question
${userText}

## Lumi's Response
${lumiText}
`;

    const words = userText.split(/\s+/).slice(0, 20).join(' ');
    const filename = words.replace(/[^a-z0-9\s]/gi, '').replace(/\s+/g, '_').toLowerCase() + '.md';

    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async (userMsg, lumiMsg) => {
    const userText = userMsg.text;
    const lumiText = lumiMsg.text;
    const timestamp = lumiMsg.timestamp.toLocaleString();

    const text = `Conversation with Lumi
Date: ${timestamp}

You:
${userText}

Lumi:
${lumiText}`;

    try {
      await navigator.clipboard.writeText(text);
      console.log('Copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const regenerateResponse = async (userMsg, oldResponseIndex) => {
    setLoading(true);

    try {
      const response = await api.post(`/chat`, {
        message: userMsg.text
      });

      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[oldResponseIndex] = {
          sender: 'lumi',
          text: response.data.response,
          timestamp: new Date()
        };
        return newMessages;
      });
    } catch (error) {
      console.error('Error regenerating response:', error);
    } finally {
      setLoading(false);
    }
  };

  const bookmarkConversation = async (eventId) => {
    if (!eventId) {
      console.log('No event ID - conversation not stored in database');
      return;
    }

    const tryBookmark = async () => {
      const response = await api.post(`/bookmark/${eventId}`);
      if (response.data.success) {
        const isBookmarked = response.data.bookmarked;
        setBookmarkedIds(prev => {
          const newSet = new Set(prev);
          if (isBookmarked) {
            newSet.add(eventId);
          } else {
            newSet.delete(eventId);
          }
          return newSet;
        });
        return true;
      }
      return false;
    };

    try {
      await tryBookmark();
    } catch (error) {
      // 404 means event is still being saved by background task — retry with backoff
      if (error.response?.status === 404) {
        const delays = [5000, 10000, 15000, 15000]; // ~45s total coverage
        for (let i = 0; i < delays.length; i++) {
          console.log(`Event still saving, retry ${i + 1}/${delays.length} in ${delays[i] / 1000}s...`);
          await new Promise(resolve => setTimeout(resolve, delays[i]));
          try {
            if (await tryBookmark()) return;
          } catch (retryError) {
            if (retryError.response?.status !== 404) {
              console.error('Bookmark retry failed:', retryError);
              return;
            }
          }
        }
        console.error('Bookmark failed: event not saved after retries');
      } else {
        console.error('Error bookmarking conversation:', error);
      }
    }
  };

  const loadBookmarks = async () => {
    setBookmarksLoading(true);
    try {
      const response = await api.get(`/bookmarks?limit=50`);
      const bookmarks = response.data.bookmarks || [];

      const bookmarkMessages = [];
      for (const bookmark of bookmarks) {
        const parsed = parseConversationText(bookmark.conversation || '');
        if (!parsed) continue;

        bookmarkMessages.push({
          sender: 'user',
          text: parsed.userPart,
          timestamp: new Date(bookmark.event_date),
          eventId: bookmark.id
        });
        bookmarkMessages.push({
          sender: 'lumi',
          text: parsed.lumiPart,
          timestamp: new Date(bookmark.event_date),
          eventId: bookmark.id
        });
      }

      setBookmarkedConversations(bookmarkMessages);
      setBookmarkedIds(new Set(bookmarks.map(b => b.id)));
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    } finally {
      setBookmarksLoading(false);
    }
  };

  const toggleBookmarksView = () => {
    if (!bookmarksVisible) {
      loadBookmarks();
    }
    setBookmarksVisible(!bookmarksVisible);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (!value.trim()) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const response = await api.get(`/search-conversations?q=${encodeURIComponent(value.trim())}&limit=20`);
        const conversations = response.data.conversations || [];

        const resultMessages = [];
        for (let i = conversations.length - 1; i >= 0; i--) {
          const conversation = conversations[i];
          const parsed = parseConversationText(conversation.conversation);
          if (!parsed) continue;

          resultMessages.push({
            sender: 'user',
            text: parsed.userPart,
            timestamp: new Date(conversation.event_date),
            eventId: conversation.id
          });
          resultMessages.push({
            sender: 'lumi',
            text: parsed.lumiPart,
            timestamp: new Date(conversation.event_date),
            eventId: conversation.id
          });
        }

        setSearchResults(resultMessages);
      } catch (error) {
        console.error('Error searching conversations:', error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const activeMessages = bookmarksVisible ? bookmarkedConversations : (searchResults !== null ? searchResults : messages);

  // Helper to render message bubbles with proper props
  const renderMessageBubble = (message, index) => {
    return (
      <MessageBubble
        key={index}
        message={message}
        index={index}
        messagesArray={activeMessages}
        bookmarkedIds={bookmarkedIds}
        formatTime={formatTime}
        exportToMarkdown={exportToMarkdown}
        copyToClipboard={copyToClipboard}
        regenerateResponse={regenerateResponse}
        bookmarkConversation={bookmarkConversation}
      />
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="app">
      <div className="chat-container">
        {/* Header */}
        <div className="chat-header">
          <div className="header-content">
            <div className="logo-section">
              <div className="logo">
                <img src="/skate.png" alt="LumiSkating" className="logo-icon-img" />
                <span className="logo-text">LumiSkating</span>
              </div>
              <p className="subtitle">Your Olympic Figure Skating Virtual Assistant</p>
            </div>
            <div className="header-actions">
              <div className="tab-navigation">
                <button
                  className={`tab-button ${activeTab === 'chat' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('chat');
                    // Load current video's conversation when switching to chat
                    if (currentVideoId) {
                      loadVideoConversation(currentVideoId);
                    }
                  }}
                >
                  <MessageCircle size={16} />
                  Coaching
                </button>
                <button
                  className={`tab-button ${activeTab === 'notes' ? 'active' : ''}`}
                  onClick={() => setActiveTab('notes')}
                >
                  <BookOpen size={16} />
                  Coach Notes
                </button>
                <button
                  className={`tab-button ${activeTab === 'video' ? 'active' : ''}`}
                  onClick={() => setActiveTab('video')}
                >
                  <Film size={16} />
                  Video Analysis
                </button>
                <button
                  className={`tab-button ${activeTab === 'planning' ? 'active' : ''}`}
                  onClick={() => setActiveTab('planning')}
                >
                  <Calendar size={16} />
                  Planning
                </button>
                <button
                  className="tab-button settings-button"
                  onClick={() => setSettingsOpen(true)}
                  title="Settings"
                >
                  <Settings size={16} />
                  Settings
                </button>
              </div>
              {/* Mobile header row with actions and logout */}
              <div className="mobile-header-row">
                {activeTab === 'chat' && (
                  <div className="chat-header-actions">
                    {searchActive ? (
                      <div className="search-bar">
                        <input
                          type="text"
                          placeholder="Search conversations..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          autoFocus
                        />
                        <button onClick={() => { setSearchActive(false); setSearchQuery(''); setSearchResults(null); setSearchLoading(false); if (searchTimerRef.current) clearTimeout(searchTimerRef.current); }}>✕</button>
                      </div>
                    ) : (
                      <>
                        <button
                          className={`header-button ${bookmarksVisible ? 'active' : ''}`}
                          onClick={toggleBookmarksView}
                          title="Bookmarks"
                        >
                          <Bookmark size={18} fill={bookmarksVisible ? 'currentColor' : 'none'} />
                        </button>
                        <button className="header-button" onClick={() => setSearchActive(true)} title="Search">
                          <Search size={18} />
                        </button>
                        <div className="status-indicator">
                          <div className="status-dot"></div>
                          <span>Online</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {/* User menu - always visible */}
                <div className="user-menu">
                  <span className="user-name">{user?.name || 'User'}</span>
                  <button
                    className="header-button logout-button"
                    onClick={logout}
                    title="Sign Out"
                  >
                    <LogOut size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area - Conditional based on active tab */}
        {activeTab === 'chat' ? (
          <>
            {/* Messages Area */}
            <div className="messages-container">
              {/* Mobile floating toolbar for search/bookmark (replaces header actions) */}
              <div className="mobile-chat-toolbar">
                {searchActive ? (
                  <div className="mobile-search-bar">
                    <Search size={14} />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      autoFocus
                    />
                    <button onClick={() => { setSearchActive(false); setSearchQuery(''); setSearchResults(null); setSearchLoading(false); if (searchTimerRef.current) clearTimeout(searchTimerRef.current); }}>✕</button>
                  </div>
                ) : (
                  <>
                    <button
                      className={`mobile-toolbar-btn ${bookmarksVisible ? 'active' : ''}`}
                      onClick={toggleBookmarksView}
                      title="Bookmarks"
                    >
                      <Bookmark size={16} fill={bookmarksVisible ? 'currentColor' : 'none'} />
                    </button>
                    <button className="mobile-toolbar-btn" onClick={() => setSearchActive(true)} title="Search">
                      <Search size={16} />
                    </button>
                  </>
                )}
              </div>
              {bookmarksVisible ? (
                <div className="messages">
                  {bookmarksLoading ? (
                    <div className="empty-bookmarks">
                      <Bookmark size={48} />
                      <p>Retrieving bookmarked conversations...</p>
                    </div>
                  ) : bookmarkedConversations.length === 0 ? (
                    <div className="empty-bookmarks">
                      <Bookmark size={48} />
                      <p>No bookmarked conversations yet</p>
                    </div>
                  ) : (
                    <>
                      {bookmarkedConversations.map((msg, idx) => renderMessageBubble(msg, idx))}
                    </>
                  )}
                </div>
              ) : (
                <div className="messages">
                  {searchLoading && (
                    <div className="search-results-indicator">Searching...</div>
                  )}
                  {!searchLoading && searchResults !== null && (
                    <div className="search-results-indicator">
                      {searchResults.length > 0
                        ? `${searchResults.length / 2} conversation${searchResults.length / 2 !== 1 ? 's' : ''} found`
                        : 'No conversations found'}
                    </div>
                  )}
                  {activeMessages.map((msg, idx) => renderMessageBubble(msg, idx))}
                  {loading && (
                    <div className="message-wrapper lumi-wrapper">
                      <div className="message-bubble lumi-bubble typing">
                        <div className="message-header">
                          <div className="sender-info">
                            <Bot size={16} /> Lumi
                          </div>
                        </div>
                        <div className="typing-indicator">
                          <div className="typing-dots">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                          <span className="typing-text">is thinking...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input Area - Only show for chat */}
            <div className="input-container">
              <div className="input-wrapper">
                <div className="input-field">
                  <MessageSquare className="input-icon" size={20} />
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={isMobile ? "Tap skate for menu, or ask" : "anything skating-related..."}
                    disabled={loading}
                    rows={1}
                    className="message-input"
                  />
                </div>
                <button 
                  onClick={sendMessage} 
                  disabled={loading || !input.trim()}
                  className="send-button"
                >
                  <Send size={18} />
                </button>
              </div>
              <div className="input-hint">
                LumiSkating AI can make can make mistakes,so double-check it. Press Enter to send • Shift+Enter for new line
              </div>
            </div>
          </>
        ) : activeTab === 'notes' ? (
          /* Coach Notes Tab */
          <div className="notes-container">
            <NotesTab />
          </div>
        ) : activeTab === 'video' ? (
          /* Video Analysis Tab */
          <div className="video-analysis-container">
            <VideoAnalysisUpload
              onAnalysisStart={handleAnalysisStart}
              onAnalysisComplete={handleAnalysisComplete}
              onVideoLoaded={handleVideoLoaded}
            />
          </div>
        ) : (
          /* Planning Tab */
          <div className="planning-container">
            <PlanningTab />
          </div>
        )}
      </div>

      {/* Settings Modal */}
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {/* Mobile Bottom Navigation - only visible on mobile via CSS */}
      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onSettingsClick={() => setSettingsOpen(true)}
      />

      {/* Mobile FAB - replaces header + bottom nav on mobile */}
      <MobileFAB
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onSettingsClick={() => setSettingsOpen(true)}
        userName={user?.name}
        onLogout={logout}
      />
    </div>
  );
}

/**
 * Loading screen component - Premium Gold Theme
 */
function LoadingScreen() {
  return (
    <div className="auth-loading gold-theme">
      <div className="auth-loading-content">
        <div className="auth-loading-logo">
          <img src="/skate.png" alt="LumiSkating" className="auth-loading-logo-img" />
        </div>
        <div className="auth-loading-brand">
          <span className="brand-name">Lumi</span><span className="brand-skating">Skating</span>
        </div>
        <div className="auth-loading-spinner">
          <div className="spinner-ring"></div>
        </div>
        <h2>Loading...</h2>
      </div>
    </div>
  );
}

/**
 * Protected Route wrapper - redirects to landing if not authenticated
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated, initializing } = useAuth();

  if (initializing) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * Public Route wrapper - redirects to /app if already authenticated
 */
function PublicRoute({ children }) {
  const { isAuthenticated, initializing } = useAuth();

  if (initializing) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/app" replace />;
  }

  return children;
}

/**
 * App content with routing
 */
function AppContent() {
  return (
    <Routes>
      {/* Landing page - public route, redirects to /app if authenticated */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        }
      />

      {/* Main app - protected route with subscription gate */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <SubscriptionGate>
              <MainApp />
            </SubscriptionGate>
          </ProtectedRoute>
        }
      />

      {/* Subscription success - handle return from Stripe checkout */}
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute>
            <SubscriptionSuccess />
          </ProtectedRoute>
        }
      />

      {/* Legal pages - accessible to everyone */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/about" element={<AboutPage />} />

      {/* Public sample analysis - no login required */}
      <Route path="/sample" element={<SampleAnalysis />} />

      {/* Password reset page - accessible to everyone (token required) */}
      <Route path="/reset-password" element={<ResetPasswordForm />} />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Root App component with BrowserRouter and AuthProvider
 */
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
          <AppContent />
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
